import { defaultOrganizationSchemaDescriptionByLanguage, defaultPageMetas } from '@constants/metas/default';
import Environment from '@config/index';
import { getLanguageByLocale, getLocaleBySubdomain, getLocalizedOriginByLocale } from '@helpers/localization';
import { AvailableSubdomains } from '@interfaces/models/environment';
import { AvailableLocales } from '@interfaces/models/locale';

const SEO_WEBSITE_NAME = 'Vestiaire Collective';
const SEO_FOUNDING_DATE = '2009';
const SEO_PARENT_URL = 'https://www.vestiairecollective.com';
const SEO_LOGO_PATH = '/assets/img/home/vestiairecollective_logo.svg?v=1';
const SEO_SAME_AS = [
  'https://www.instagram.com/vestiaireco',
  'https://www.facebook.com/vestiaireco',
  'https://www.linkedin.com/company/vestiaireco/',
  'https://www.youtube.com/user/VestiaireCollective',
  'https://www.threads.com/@vestiaireco',
  'https://www.tiktok.com/@vestiairecollective',
  'https://www.pinterest.com/vestiaireco/',
  'https://en.wikipedia.org/wiki/Vestiaire_Collective',
  'https://fr.wikipedia.org/wiki/Vestiaire_Collective',
  'https://www.wikidata.org/wiki/Q16682468',
  'https://www.crunchbase.com/organization/vestiairedecopines',
  'https://pitchbook.com/profiles/company/55208-17',
  'https://www.bloomberg.com/profile/company/1027883D:FP',
  'https://www.trustpilot.com/review/vestiairecollective.com',
  'https://annuaire-entreprises.data.gouv.fr/entreprise/vestiaire-collective-vestiaire-de-copines-517465225',
  'https://data.inpi.fr/entreprises/517465225',
  'https://apps.apple.com/us/app/vestiaire-collective-buy-sell/id380974668',
  'https://play.google.com/store/apps/details?id=fr.vestiairecollective',
] as const;

type PostalAddress = {
  '@type': 'PostalAddress';
  streetAddress: string;
  addressLocality: string;
  addressCountry: string;
  addressRegion?: string;
  postalCode?: string;
};

type OrganizationEntity = {
  legalName: string;
  address: PostalAddress;
  vatID?: string;
  taxID?: string;
  isParent?: boolean;
};

const PARENT_ENTITY: OrganizationEntity = {
  legalName: 'Vestiaire Collective SA',
  vatID: 'FR24517465225',
  isParent: true,
  address: {
    '@type': 'PostalAddress',
    streetAddress: '53 rue de Chateaudun',
    addressLocality: 'Paris',
    addressRegion: 'Ile-de-France',
    postalCode: '75009',
    addressCountry: 'FR',
  },
};

const ENTITY_BY_LOCALE: Record<AvailableLocales, OrganizationEntity> = {
  'fr-FR': PARENT_ENTITY,
  'es-ES': PARENT_ENTITY,
  'nl-NL': PARENT_ENTITY,
  'sv-SE': PARENT_ENTITY,
  'de-DE': {
    legalName: 'Vestiaire Collective GmbH',
    vatID: 'DE305700322',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Erika-Mann-Strasse 5',
      addressLocality: 'Munchen',
      addressRegion: 'Bavaria',
      postalCode: '80636',
      addressCountry: 'DE',
    },
  },
  'it-IT': {
    legalName: 'Vestiaire Collective Italia S.R.L',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Via Francesco Soave 6',
      addressLocality: 'Milano',
      addressRegion: 'Lombardia',
      postalCode: '20135',
      addressCountry: 'IT',
    },
  },
  'en-GB': {
    legalName: 'Vestiaire Collective UK Ltd.',
    vatID: '141776702',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '249 Silbury Boulevard',
      addressLocality: 'Milton Keynes',
      addressRegion: 'Buckinghamshire',
      postalCode: 'MK9 1NA',
      addressCountry: 'GB',
    },
  },
  'en-US': {
    legalName: 'Vestiaire Collective Americas Inc',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '850 New Barton Road, Suite 201',
      addressLocality: 'Dover',
      addressRegion: 'Delaware',
      postalCode: '19904',
      addressCountry: 'US',
    },
  },
  'zh-HK': {
    legalName: 'Vestiaire Collective HK Ltd',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Unit C & D, 11/F, China Merchants Logistics Center, 38 Hong Wan Road',
      addressLocality: 'Tsing Yi',
      addressCountry: 'HK',
    },
  },
  'ko-KR': {
    legalName: 'Vestiaire Collective Korea Ltd',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '10th Floor 507 Taehaeran-ro, Gangnam-gu',
      addressLocality: 'Seoul',
      addressCountry: 'KR',
    },
  },
};

const getSeoCdnFolder = (): string => {
  if (Environment.envName === 'teod') {
    return process.env.NEXT_PUBLIC_CDN_FOLDER ?? Environment.cdnFolder ?? '';
  }

  return Environment.cdnFolder ?? '';
};

export const getSeoBaseUrlFromLocale = (locale?: string): string => {
  const normalizedLocale = locale as AvailableLocales | undefined;
  return getLocalizedOriginByLocale(Environment.baseHost, normalizedLocale);
};

export const getSeoLogoUrl = (locale?: string): string => {
  const cdnFolder = getSeoCdnFolder();
  const logoBaseUrl = cdnFolder || getSeoBaseUrlFromLocale(locale);

  return `${logoBaseUrl}${SEO_LOGO_PATH}`;
};

const getLocaleBySubdomainFromHost = (): AvailableLocales | undefined => {
  if (!Environment.baseHost) {
    return undefined;
  }

  try {
    const subdomain = new URL(Environment.baseHost).hostname.split('.')[0] as AvailableSubdomains;
    return getLocaleBySubdomain(subdomain);
  } catch {
    return undefined;
  }
};

const getSeoEntityFromLocale = (locale?: string): OrganizationEntity => {
  const normalizedLocale = (locale as AvailableLocales | undefined) ?? getLocaleBySubdomainFromHost();

  if (!normalizedLocale) {
    return PARENT_ENTITY;
  }

  return ENTITY_BY_LOCALE[normalizedLocale] ?? PARENT_ENTITY;
};

const getSeoOrganizationDescriptionFromLocale = (locale?: string): string => {
  const normalizedLocale = (locale as AvailableLocales | undefined) ?? getLocaleBySubdomainFromHost();
  const englishOrganizationDescription =
    defaultOrganizationSchemaDescriptionByLanguage.en ?? defaultPageMetas.en.description;

  if (!normalizedLocale) {
    return englishOrganizationDescription;
  }

  const language = getLanguageByLocale(normalizedLocale);
  return defaultOrganizationSchemaDescriptionByLanguage[language] ?? englishOrganizationDescription;
};

const getSeoOrganizationIdFromLocale = (locale?: string): string => {
  return `${getSeoBaseUrlFromLocale(locale)}/#organization`;
};

export const getSeoOrganizationSchemaFromLocale = (locale?: string) => {
  const base = getSeoBaseUrlFromLocale(locale);
  const description = getSeoOrganizationDescriptionFromLocale(locale);
  const entity = getSeoEntityFromLocale(locale);

  return {
    '@context': 'https://schema.org',
    '@type': 'OnlineStore',
    '@id': getSeoOrganizationIdFromLocale(locale),
    name: SEO_WEBSITE_NAME,
    legalName: entity.legalName,
    url: base,
    logo: {
      '@type': 'ImageObject',
      url: getSeoLogoUrl(locale),
    },
    address: entity.address,
    ...(entity.vatID && { vatID: entity.vatID }),
    ...(entity.taxID && { taxID: entity.taxID }),
    ...(!entity.isParent && {
      parentOrganization: {
        '@type': 'Organization',
        name: SEO_WEBSITE_NAME,
        legalName: PARENT_ENTITY.legalName,
        url: SEO_PARENT_URL,
        vatID: PARENT_ENTITY.vatID,
      },
    }),
    foundingDate: SEO_FOUNDING_DATE,
    description,
    sameAs: SEO_SAME_AS,
  };
};
